import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Session, Participant, Restaurant, Match } from '../types';
import { sessionApi } from '../services/api';

interface SessionContextType {
  session: Session | null;
  participantId: string | null;
  restaurants: Restaurant[];
  matches: Match[];
  loading: boolean;
  error: string | null;
  setParticipantId: (id: string) => void;
  loadSession: (sessionId: string) => Promise<void>;
  loadRestaurants: (sessionId: string, participantId: string) => Promise<void>;
  loadMatches: (sessionId: string) => Promise<void>;
  addParticipant: (participant: Participant) => void;
  addMatch: (match: Match) => void;
  removeRestaurant: (restaurantId: string) => void;
  clearError: () => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const loadSession = useCallback(async (sessionId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await sessionApi.get(sessionId);
      setSession(data.session);
    } catch (err: any) {
      setError(err.message || 'Не удалось загрузить сессию');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRestaurants = useCallback(async (sessionId: string, pid: string) => {
    setLoading(true);
    try {
      const data = await sessionApi.getRestaurants(sessionId, pid);
      setRestaurants(data.restaurants);
    } catch (err: any) {
      setError(err.message || 'Не удалось загрузить рестораны');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMatches = useCallback(async (sessionId: string) => {
    try {
      const data = await sessionApi.getMatches(sessionId);
      setMatches(data.matches);
    } catch (err: any) {
      setError(err.message || 'Не удалось загрузить совпадения');
    }
  }, []);

  const addParticipant = useCallback((participant: Participant) => {
    setSession((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        participants: [...prev.participants, participant],
      };
    });
  }, []);

  const addMatch = useCallback((match: Match) => {
    setMatches((prev) => [...prev, match]);
  }, []);

  const removeRestaurant = useCallback((restaurantId: string) => {
    setRestaurants((prev) => prev.filter((r) => r.id !== restaurantId));
  }, []);

  return (
    <SessionContext.Provider
      value={{
        session,
        participantId,
        restaurants,
        matches,
        loading,
        error,
        setParticipantId,
        loadSession,
        loadRestaurants,
        loadMatches,
        addParticipant,
        addMatch,
        removeRestaurant,
        clearError,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};
